import React, { useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-regular-svg-icons';

import DealForm from 'bundles/apex/deals/form';
import LoadingSpinner from 'components/ui/loading_spinner';
import NewTasks from 'bundles/apex/deals/form_modal/new_tasks';
import { Button } from 'components/ui/button';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { ExtractionInputs } from './inputs';
import { isFileReference } from './types';
import { Modal } from '@buildoutinc/blueprint-react/ui/Modal';
import { get, post } from 'helpers/api';
import { showSnackbar } from 'components/ui/snackbar/helper';

import type { Deal } from 'bundles/apex/deals/types';
import type { Config, DealFile, DealFileReference, Options } from './types';

const CONFIG_PATH = '/apex/deals/build_with_ai_config';

interface Props {
  autoSubmit?: boolean;
  initialAttachment?: DealFileReference | null;
  initialDealType?: string;
  initialStage?: string;
  initialText?: string;
  open: boolean;
  onClose: () => void;
  onCreate: (deal: Deal) => void;
}

export function CreateDealWithAiModal({
  autoSubmit = false,
  initialAttachment,
  initialDealType,
  initialStage,
  initialText,
  open,
  onClose,
  onCreate,
}: Props) {
  const [config, setConfig] = useState<Config | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [deal, setDeal] = useState<Deal | null>(null);
  const [savedDeal, setSavedDeal] = useState<Deal | null>(null);
  const [file, setFile] = useState<DealFile | null>(null);
  const [loading, setLoading] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);

  useEffect(() => {
    if (!open || config || configLoading) {
      return;
    }

    void loadConfig();
  }, [open]);

  useEffect(() => {
    if (initialAttachment) {
      setFile(initialAttachment);
    }
  }, [initialAttachment]);

  async function loadConfig() {
    setConfigLoading(true);
    try {
      setConfig(await get(CONFIG_PATH));
    } catch {
      showSnackbar({ text: 'Could not load deal creation', variant: 'danger' });
      onClose();
    } finally {
      setConfigLoading(false);
    }
  }

  function handleStart() {
    setDeal(null);
    setLoading(true);
  }

  function handleFinish(deal: Deal | null) {
    setDeal(deal);
    setLoading(false);
  }

  async function handleSave(deal: Deal) {
    await attachFileToDeal(deal);
    setSavedDeal(deal);
  }

  function handleNewTasksFinish(deal: Deal) {
    onCreate(deal);
    close();
  }

  function handleClose() {
    if (savedDeal) {
      onCreate(savedDeal);
    }

    close();
  }

  async function attachFileToDeal(deal: Deal) {
    if (!file || !deal?.voucherId) {
      return;
    }

    const formData = new FormData();
    formData.append('attachable_type', 'Backoffice::Voucher');
    formData.append('attachable_id', deal.voucherId);
    if (isFileReference(file)) {
      formData.append('ai_chat_attachment_signed_id', file.signedId);
    } else {
      formData.append('data', file);
    }

    try {
      await post('/attachments', formData);
    } catch {
      showSnackbar({
        text: 'The deal was saved, but attaching the PDF failed.',
        variant: 'warning',
      });
    }
  }

  function close() {
    setDeal(null);
    setSavedDeal(null);
    setFile(null);
    setLoading(false);
    setAiEnabled(true);
    onClose();
  }

  async function enableManualEntry() {
    if (!config) {
      return;
    }

    if (!deal) {
      try {
        const blankDeal = await get(config.paths.newDeal);
        setDeal(blankDeal);
      } catch {
        showSnackbar({ text: 'Could not start manual deal creation', variant: 'danger' });
      }
    }

    setAiEnabled(false);
  }

  if (!config) {
    return (
      <Modal open={open} onClose={onClose}>
        <Modal.Content size="lg">
          <Modal.Body>
            {open &&
              <div className="w-100" style={{ minHeight: 400 }}>
                <LoadingSpinner size="xl" skipTopBuffer />
              </div>
            }
          </Modal.Body>
        </Modal.Content>
      </Modal>
    );
  }

  const { features, forUser, paths, stagesConfig } = config;
  const options: Options = {
    ...config.options,
    stage: Object.values(stagesConfig).map(stageConfig => ({ label: stageConfig.label, value: stageConfig.key })),
  };

  // The deal form renders its own "footer", so this belongs in different places depending on state
  const aiToggle = (
    <Button variant="ghost" onClick={aiEnabled ? enableManualEntry : () => setAiEnabled(true)}>
      {aiEnabled
        ? 'Create Deal Manually'
        : 'Create with AI'
      }
    </Button>
  );

  return (
    <Modal open={open} onClose={handleClose}>
      <Modal.Content scrollable size="lg">
        <Modal.Header>
          <Modal.Title>{savedDeal ? 'Tasks Created' : 'Create a Deal'}</Modal.Title>
          {!savedDeal &&
            <Modal.Description>Upload deal documents to get your deal started, or create manually.</Modal.Description>
          }
        </Modal.Header>

        <Modal.Body>
          {savedDeal ? (
            <NewTasks
              adminedOfficeIds={forUser.adminedOfficeIds}
              apexAdmin={forUser.apexAdmin}
              attachmentsPath={paths.attachments}
              canDeleteAttachments={!!forUser.admin}
              fieldsPath={paths.dealCreationFields}
              newDeal={savedDeal}
              oldDeal={deal}
              requiredTasksEnabled={features.requiredTasks}
              users={options.users}
              onHide={handleNewTasksFinish}
            />
          ) : (
            <>
              <div className="d-flex gap-3" style={{ maxHeight: 'calc(100vh - 10rem)' }}>
                {aiEnabled &&
                  <div className="d-flex flex-column gap-3 flex-fill" style={{ maxWidth: 336 }}>
                    <ExtractionInputs
                      autoSubmit={autoSubmit && (!initialAttachment || !!file)}
                      deal={deal}
                      file={file}
                      initialDealType={initialDealType}
                      initialStage={initialStage}
                      initialText={initialText}
                      options={options}
                      paths={paths}
                      onFileChange={setFile}
                      onFinish={handleFinish}
                      onStart={handleStart}
                    />

                    <span className="text-muted fs-small">
                      AI can make mistakes. Please review before saving.
                    </span>

                    {deal &&
                      <div className="d-flex align-items-end flex-fill">
                        {aiToggle}
                      </div>
                    }
                  </div>
                }

                <div className="d-flex flex-column flex-fill min-h-0">
                  {deal &&
                    <div className="d-flex flex-column flex-fill min-h-0">
                      <DealForm
                        deal={deal}
                        extraButton={aiEnabled ? undefined : aiToggle}
                        features={features}
                        forUser={forUser}
                        layout="full"
                        options={options}
                        paths={{ fields: paths.dealCreationFields }}
                        stagesConfig={stagesConfig}
                        onCancel={close}
                        onSave={handleSave}
                      />
                    </div>
                  }

                  {!deal && (
                    <div className="w-100 h-100" style={{ minHeight: 400 }}>
                      {loading ? <LoadingSpinner size="xl" skipTopBuffer /> :
                        <Empty className="h-100">
                          <Empty.Media>
                            <FontAwesomeIcon icon={faSparkles} />
                          </Empty.Media>
                          <Empty.Title>
                            Deal will appear here
                          </Empty.Title>
                          <Empty.Content>
                            After uploading, the deal will appear here.
                          </Empty.Content>
                        </Empty>
                      }
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        {!deal && aiEnabled && (
          <Modal.Footer>
            <div className="d-flex justify-content-between w-100">
              {aiToggle}
              <Button label="Cancel" variant="ghost" onClick={close} />
            </div>
          </Modal.Footer>
        )}
      </Modal.Content>
    </Modal>
  );
}
